/**
 * Templates AI Edit Chat Slice
 *
 * Redux slice for managing template build chat state, messages, and interrupt workflow.
 * Uses the shared slice factory from ai-chat-core with template-specific extensions.
 */

import {
  type ActivityType,
  type AssistantStatus,
  type ChoiceOption,
  type ConfirmationNamespace,
  type ConfirmationType,
  createInitialState,
  type EditPreview,
  type InterruptEventType,
  type SSEPerformanceMetrics,
  type StepPayload,
} from '@features/ai-chat-core';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  type BuildStatus,
  type DraftTemplate,
  draftTemplateSignature,
  type TemplateBuildState,
} from '../types';

// ============================================================================
// Initial State
// ============================================================================

const baseInitialState = createInitialState();

const initialState: TemplateBuildState = {
  ...baseInitialState,
  // Build session fields
  taskId: null,
  buildStatus: 'idle',
  progressMessage: null,
  // Document metadata (for PDFs)
  documentTitle: null,
  documentSummary: null,
  // Draft template
  draftTemplate: null,
  // Section selection
  selectedSectionId: null,
  // Template this session was seeded from
  sourceTemplateId: null,
  // After save
  createdTemplateId: null,
  savedDraftSignature: null,
  // Status refetch flag
  needsStatusRefetch: false,
};

// ============================================================================
// Slice
// ============================================================================

export const templateBuildSlice = createSlice({
  name: 'templatesAiEdit/ui',
  initialState,
  reducers: {
    // ========================================================================
    // Session Management
    // ========================================================================

    initializeTemplateBuild: (
      state,
      action: PayloadAction<{ taskId?: string | null; threadId?: string | null }>,
    ) => {
      state.taskId = action.payload.taskId ?? null;
      state.threadId = action.payload.threadId ?? null;
      state.messages = [];
      state.error = null;
      state.assistant.status = 'idle';
      state.draftTemplate = null;
      state.selectedSectionId = null;
      state.buildStatus = action.payload.taskId ? 'processing' : 'idle';
      state.progressMessage = null;
      state.documentTitle = null;
      state.documentSummary = null;
      state.sourceTemplateId = null;
      state.createdTemplateId = null;
      state.savedDraftSignature = null;
    },

    /**
     * Move the session onto the copy a save just created.
     *
     * A build task publishes by forking from its source every time, so a
     * session seeded from a public template would spawn a new copy on each
     * save. Re-point at the copy — which the provider now owns, and which
     * therefore updates in place — so the rest of the session lands in one
     * template (GDN-3099).
     *
     * The task changes, so the thread does too. Rather than emptying the chat
     * with no explanation, seed it with one.
     */
    handOffToForkedTemplate: (
      state,
      action: PayloadAction<{ taskId: string; templateId: string; notice: string }>,
    ) => {
      const { taskId, templateId, notice } = action.payload;

      state.taskId = taskId;
      state.threadId = null;
      state.error = null;
      state.assistant.status = 'idle';
      state.draftTemplate = null;
      state.selectedSectionId = null;
      state.buildStatus = 'processing';
      state.progressMessage = null;
      state.documentTitle = null;
      state.documentSummary = null;
      // Both point at the copy now: it seeds this session and it is what a
      // further save updates.
      state.sourceTemplateId = templateId;
      state.createdTemplateId = templateId;
      // Re-derived against the copy by useHydrateSavedDraftSignature once the
      // new task's draft arrives.
      state.savedDraftSignature = null;
      state.messages = [
        {
          id: `handoff-${taskId}`,
          role: 'assistant',
          content: notice,
          timestamp: new Date().toISOString(),
        },
      ];
    },

    setTaskId: (state, action: PayloadAction<string>) => {
      state.taskId = action.payload;
    },

    setThreadId: (state, action: PayloadAction<string>) => {
      state.threadId = action.payload;
    },

    // ========================================================================
    // Build Status Management
    // ========================================================================

    setBuildStatus: (state, action: PayloadAction<BuildStatus>) => {
      state.buildStatus = action.payload;
    },

    setProgressMessage: (state, action: PayloadAction<string | null>) => {
      state.progressMessage = action.payload;
    },

    setDocumentTitle: (state, action: PayloadAction<string | null>) => {
      state.documentTitle = action.payload;
    },

    setDocumentSummary: (state, action: PayloadAction<string | null>) => {
      state.documentSummary = action.payload;
    },

    /**
     * Record which template this session writes to. Deliberately does NOT end
     * the session — saving used to flip the status to 'completed', which
     * unmounted the preview and the chat at the exact moment the provider was
     * asking "did my change save?" (GDN-3099). Callers that genuinely mean
     * "this build is over" dispatch setBuildStatus('completed') themselves.
     */
    setCreatedTemplateId: (state, action: PayloadAction<string>) => {
      state.createdTemplateId = action.payload;
    },

    /**
     * Fingerprint the draft as published. Anything the provider changes after
     * this makes the draft dirty again.
     */
    markDraftSaved: state => {
      state.savedDraftSignature = state.draftTemplate
        ? draftTemplateSignature(state.draftTemplate)
        : null;
    },

    /**
     * Adopt a fingerprint computed elsewhere — specifically, from the revision
     * the server has actually published. Resuming a task cannot infer saved-ness
     * from `created_template_id` alone: that only says the task published at
     * some point, not that the draft on screen is what got published.
     */
    setSavedDraftSignature: (state, action: PayloadAction<string | null>) => {
      state.savedDraftSignature = action.payload;
    },

    setSourceTemplateId: (state, action: PayloadAction<string | null>) => {
      state.sourceTemplateId = action.payload;
    },

    setNeedsStatusRefetch: (state, action: PayloadAction<boolean>) => {
      state.needsStatusRefetch = action.payload;
    },

    // ========================================================================
    // Draft Template Management
    // ========================================================================

    setDraftTemplate: (state, action: PayloadAction<DraftTemplate>) => {
      state.draftTemplate = action.payload;
    },

    clearDraftTemplate: state => {
      state.draftTemplate = null;
    },

    // ========================================================================
    // Section Selection
    // ========================================================================

    selectSection: (state, action: PayloadAction<string | null>) => {
      state.selectedSectionId = action.payload;
    },

    // ========================================================================
    // Message Management
    // ========================================================================

    addUserMessage: (state, action: PayloadAction<string>) => {
      const now = new Date();
      state.messages.push({
        id: `user-${now.getTime()}`,
        role: 'user',
        content: action.payload,
        timestamp: now.toISOString(),
      });
    },

    // ========================================================================
    // Streaming State
    // ========================================================================

    setAssistantStatus: (state, action: PayloadAction<AssistantStatus>) => {
      state.assistant.status = action.payload;
    },

    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.assistant.currentStep = action.payload;
    },

    startStreamingMessage: (state, action: PayloadAction<string>) => {
      state.assistant.currentMessageId = action.payload;
      state.assistant.streamingContent = '';
      state.assistant.firstTokenReceived = false;
      state.assistant.status = 'streaming';
    },

    appendStreamingContent: (state, action: PayloadAction<string>) => {
      state.assistant.streamingContent += action.payload;
      if (!state.assistant.firstTokenReceived) {
        state.assistant.firstTokenReceived = true;
      }
    },

    finalizeMessage: state => {
      if (state.assistant.streamingContent) {
        const now = new Date();
        state.messages.push({
          id: state.assistant.currentMessageId || `assistant-${now.getTime()}`,
          role: 'assistant',
          content: state.assistant.streamingContent,
          timestamp: now.toISOString(),
        });
      }
      state.assistant.streamingContent = '';
      state.assistant.currentMessageId = null;
      state.assistant.firstTokenReceived = false;
      state.assistant.status = 'thinking';
    },

    // ========================================================================
    // Activity Management
    // ========================================================================

    setActivitySnapshot: (
      state,
      action: PayloadAction<{
        messageId: string;
        activityType: ActivityType;
        message: string;
        timestamp: number;
        replace?: boolean;
      }>,
    ) => {
      const { messageId, activityType, message, timestamp, replace = true } = action.payload;

      if (!replace && state.activities[messageId]) {
        return;
      }

      state.activities[messageId] = {
        messageId,
        activityType,
        message,
        timestamp,
      };
    },

    clearActivity: (state, action: PayloadAction<string>) => {
      const messageId = action.payload;
      const { [messageId]: _removed, ...remaining } = state.activities;
      state.activities = remaining;
    },

    clearAllActivities: state => {
      state.activities = {};
    },

    // ========================================================================
    // Entity Updates (not heavily used for templates, but included for consistency)
    // ========================================================================

    startEntityStreaming: (state, action: PayloadAction<string>) => {
      const entityId = action.payload;
      state.entityUpdates[entityId] = '';
      if (!state.streamingEntities.includes(entityId)) {
        state.streamingEntities.push(entityId);
      }
    },

    appendEntityContent: (
      state,
      action: PayloadAction<{ entityId: string; content: string }>,
    ) => {
      const { entityId, content } = action.payload;
      if (state.entityUpdates[entityId] !== undefined) {
        state.entityUpdates[entityId] += content;
      } else {
        state.entityUpdates[entityId] = content;
      }
    },

    finalizeEntityStreaming: (state, action: PayloadAction<string>) => {
      const entityId = action.payload;
      state.streamingEntities = state.streamingEntities.filter(id => id !== entityId);
    },

    clearAllEntityUpdates: state => {
      state.entityUpdates = {};
      state.streamingEntities = [];
    },

    markEntityAffected: (state, action: PayloadAction<string>) => {
      const entityId = action.payload;
      if (!state.affectedEntityIds.includes(entityId)) {
        state.affectedEntityIds.push(entityId);
      }
    },

    // ========================================================================
    // Interrupt Management
    // ========================================================================

    showInterrupt: (
      state,
      action: PayloadAction<{
        interruptId: string;
        eventType: InterruptEventType;
        confirmLabel?: string;
        cancelLabel?: string;
        message?: string;
        edits?: EditPreview[];
        namespace?: ConfirmationNamespace;
        confirmationType?: ConfirmationType;
        question?: string;
        options?: ChoiceOption[];
        allowMultiple?: boolean;
        allowSelectAll?: boolean;
        minSelections?: number;
        maxSelections?: number;
        allowTextInput?: boolean;
        textInputPlaceholder?: string;
        steps?: StepPayload[];
        title?: string;
        detectedIntent?: string;
        originalMessage?: string;
        metadata?: unknown;
      }>,
    ) => {
      const { interruptId, eventType, ...fields } = action.payload;

      state.interrupt = {
        isActive: true,
        interruptId,
        eventType,
        selectedOptionIds: [],
        currentStepIndex: eventType === 'MULTI_STEP' ? 0 : undefined,
        stepResponses: eventType === 'MULTI_STEP' ? {} : undefined,
        ...fields,
      };

      if (eventType === 'CONFIRMATION') {
        state.assistant.status = 'awaiting_confirmation';
      } else {
        state.assistant.status = 'awaiting_selection';
      }
    },

    toggleOptionSelection: (state, action: PayloadAction<string>) => {
      if (!state.interrupt) return;

      const optionId = action.payload;
      const selectedIds = state.interrupt.selectedOptionIds;

      if (state.interrupt.eventType === 'CONFIRMATION') {
        state.interrupt.selectedOptionIds = [optionId];
        return;
      }

      if (selectedIds.includes(optionId)) {
        state.interrupt.selectedOptionIds = selectedIds.filter(id => id !== optionId);
      } else {
        state.interrupt.selectedOptionIds.push(optionId);
      }
    },

    selectAllOptions: state => {
      if (!state.interrupt || state.interrupt.eventType !== 'MULTIPLE_CHOICE') return;
      if (!state.interrupt.options) return;

      state.interrupt.selectedOptionIds = state.interrupt.options.map(opt => opt.id);
    },

    deselectAllOptions: state => {
      if (!state.interrupt || state.interrupt.eventType !== 'MULTIPLE_CHOICE') return;

      state.interrupt.selectedOptionIds = [];
    },

    setStepResponse: (
      state,
      action: PayloadAction<{
        stepId: string;
        selectedIds?: string[];
        confirmed?: boolean;
      }>,
    ) => {
      if (!state.interrupt || state.interrupt.eventType !== 'MULTI_STEP') return;

      const { stepId, selectedIds, confirmed } = action.payload;
      if (!state.interrupt.stepResponses) {
        state.interrupt.stepResponses = {};
      }

      state.interrupt.stepResponses[stepId] = {
        stepId,
        selectedIds,
        confirmed,
      };
    },

    setCurrentStepIndex: (state, action: PayloadAction<number>) => {
      if (!state.interrupt || state.interrupt.eventType !== 'MULTI_STEP') return;

      state.interrupt.currentStepIndex = action.payload;
    },

    clearInterrupt: state => {
      state.interrupt = null;
    },

    // ========================================================================
    // UI State Management
    // ========================================================================

    setChatVisibility: (state, action: PayloadAction<boolean>) => {
      state.isChatVisible = action.payload;
    },

    toggleChatVisibility: state => {
      state.isChatVisible = !state.isChatVisible;
    },

    // ========================================================================
    // Workflow Completion
    // ========================================================================

    completeWorkflow: state => {
      state.assistant.status = 'idle';
      state.assistant.currentStep = null;
      state.interrupt = null;
      state.streamingEntities = [];
    },

    // ========================================================================
    // Error Handling
    // ========================================================================

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.assistant.status = 'idle';
      state.entityUpdates = {};
      state.streamingEntities = [];
    },

    clearError: state => {
      state.error = null;
    },

    // ========================================================================
    // SSE Performance Tracking
    // ========================================================================

    setSSEPerformanceMetrics: (state, action: PayloadAction<SSEPerformanceMetrics>) => {
      state.ssePerformanceMetrics = action.payload;
    },

    clearSSEPerformanceMetrics: state => {
      state.ssePerformanceMetrics = null;
    },

    // ========================================================================
    // Reset
    // ========================================================================

    resetBuild: () => initialState,
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  initializeTemplateBuild,
  setTaskId,
  setThreadId,
  setBuildStatus,
  setProgressMessage,
  setDocumentTitle,
  setDocumentSummary,
  setCreatedTemplateId,
  setNeedsStatusRefetch,
  handOffToForkedTemplate,
  markDraftSaved,
  setSourceTemplateId,
  setSavedDraftSignature,
  setDraftTemplate,
  clearDraftTemplate,
  selectSection,
  addUserMessage,
  setAssistantStatus,
  setCurrentStep,
  startStreamingMessage,
  appendStreamingContent,
  finalizeMessage,
  setActivitySnapshot,
  clearActivity,
  clearAllActivities,
  startEntityStreaming,
  appendEntityContent,
  finalizeEntityStreaming,
  clearAllEntityUpdates,
  markEntityAffected,
  showInterrupt,
  toggleOptionSelection,
  selectAllOptions,
  deselectAllOptions,
  setStepResponse,
  setCurrentStepIndex,
  clearInterrupt,
  setChatVisibility,
  toggleChatVisibility,
  completeWorkflow,
  setError,
  clearError,
  setSSEPerformanceMetrics,
  clearSSEPerformanceMetrics,
  resetBuild,
} = templateBuildSlice.actions;

export default templateBuildSlice.reducer;

